import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useCentinelaCases,
  useCentinelaCatalogs,
  useCentinelaUnits,
} from "../../../features/centinela/queries";
import { useGeofencesList } from "../../../features/geofences/queries";
import { useRoutesList } from "../../../features/routes/queries";
import { useTripsList } from "../../../features/trips/queries";
import type {
  CentinelaMessageTone,
  CentinelaUnit,
  CentinelaUnitTrip,
} from "../../../features/centinela/types";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import type { Geofence } from "../../../store/admin/geofencesStore";
import type { Unit } from "../../../store/admin/unitsStore";
import {
  mapCentinelaCases,
  tallyThreats,
  worstOpenCaseByDevice,
  type CentinelaCaseView,
} from "../services/mapCentinelaCase";
import {
  byWatchSeverity,
  geofenceGeometry,
  routePath,
  type GeofenceGeometry,
  type LatLngLiteral,
} from "../services/tacticalMap";
import type { UnitChip, WatchStatus } from "../types";
import { useWatchedFleet } from "./useWatchedFleet";

const CASES_LIMIT = 120;
const GEOFENCES_LIMIT = 100;
const ROUTES_LIMIT = 100;
const TRIPS_LIMIT = 60;

export interface MonitoringUnitRow {
  unitId: string;
  deviceId: string | null;
  name: string;
  plate: string | null;
  driver: string | null;
  status: WatchStatus;
  speedKph: number | null;
  /** Rumbo en grados del último reporte. `null` si el equipo no lo manda. */
  headingDeg: number | null;
  /** Foto del expediente del conductor, si el conductor la tiene. */
  driverPhoto: string | null;
  address: string | null;
  updatedAt: string | null;
  position: LatLngLiteral | null;
  hasOperatorPhone: boolean;
  underCustody: boolean;
  isCalling: boolean;
  fuelPercent: number;
  chips: UnitChip[];
  aiMessage: string;
  aiMessageType: CentinelaMessageTone;
  openCase: CentinelaCaseView | null;
  openCaseLabel: string | null;
  trip: CentinelaUnitTrip | null;
  unit: Unit;
}

export interface MonitoringThreat {
  id: string;
  label: string;
  open: number;
  total: number;
  deviceIds: Set<string>;
}

export interface MonitoringGeofence {
  id: string;
  name: string;
  type: string;
  geometry: GeofenceGeometry;
  source: Geofence;
}

export interface MonitoringCorridor {
  id: string;
  name: string;
  path: LatLngLiteral[];
}

/**
 * Ordena por a quién le toca actuar, no por cuándo pasó.
 *
 * `requiresHuman` es el que Centinela ya no puede resolver solo: llamó, no le
 * contestaron, o el tipo de alerta exige que una persona confirme. Dejarlo
 * hundido entre casos que el sistema está atendiendo es cómo se pierde el
 * único que pedía algo.
 */
function ordenarPorAtencion(lista: CentinelaCaseView[]): CentinelaCaseView[] {
  const peso = (c: CentinelaCaseView) => {
    if (c.requiresHuman && c.isOpen) return 0;
    if (c.isOpen) return 1;
    return 2;
  };
  return [...lista].sort((a, b) => {
    const d = peso(a) - peso(b);
    if (d !== 0) return d;
    // A igual urgencia, lo más reciente arriba: es el orden con el que ya se
    // leía la lista y no hay por qué cambiarlo.
    //
    // `openedAt` es una cadena ISO, no un número. Restarlas da NaN y `sort`
    // con NaN deja el arreglo en un orden arbitrario que PARECE correcto
    // mientras la lista es corta — el peor tipo de error. Se comparan como
    // texto, que en ISO es lo mismo que comparar fechas.
    return String(b.openedAt || '').localeCompare(String(a.openedAt || ''));
  });
}

export function useMonitoringCenter() {
  const { t } = useTranslation("common");
  const companyId = useCompaniesStore((state) => state.companyId);
  const fleet = useWatchedFleet();
  const centinelaUnits = useCentinelaUnits();
  const casesQuery = useCentinelaCases({ limit: CASES_LIMIT });
  const catalogsQuery = useCentinelaCatalogs();
  const geofencesQuery = useGeofencesList({ page: 1, limit: GEOFENCES_LIMIT });
  const routesQuery = useRoutesList({ page: 1, limit: ROUTES_LIMIT });
  const tripsQuery = useTripsList({ page: 1, limit: TRIPS_LIMIT });

  const [threatId, setThreatId] = useState<string | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);

  useEffect(() => {
    setThreatId(null);
    setSelectedUnitId(null);
    setSelectedCaseId(null);
  }, [companyId]);

  /**
   * ── Lo que espera a alguien va ARRIBA ──────────────────────────────────
   *
   * La lista venía por hora de apertura, así que un caso que pide intervención
   * humana se hundía en cuanto se abrían dos casos nuevos —y los nuevos se
   * abren solos, todo el tiempo—. El único de la lista que necesita que una
   * persona haga algo terminaba fuera de la vista, debajo de los que Centinela
   * ya está atendiendo.
   *
   * El orden es: primero los que esperan a una persona, después los abiertos,
   * y dentro de cada grupo lo más reciente arriba. La hora sigue decidiendo,
   * pero después de la urgencia real.
   */
  const cases = useMemo(
    () => ordenarPorAtencion(mapCentinelaCases(casesQuery.data?.cases)),
    [casesQuery.data],
  );

  const openCases = useMemo(() => cases.filter((item) => item.isOpen), [cases]);

  const openCaseByDevice = useMemo(() => worstOpenCaseByDevice(cases), [cases]);

  const threatLabels = useMemo(() => {
    const labels = new Map<string, string>();
    for (const alert of catalogsQuery.data?.alert_types ?? []) labels.set(alert.id, alert.label);
    return labels;
  }, [catalogsQuery.data]);

  const threats = useMemo<MonitoringThreat[]>(() => {
    const tally = tallyThreats(cases);
    return [...tally.entries()]
      .map(([id, row]) => ({
        id,
        label: threatLabels.get(id) ?? id,
        open: row.open,
        total: row.total,
        deviceIds: row.deviceIds,
      }))
      .sort((a, b) => b.open - a.open || b.total - a.total || a.label.localeCompare(b.label));
  }, [cases, threatLabels]);

  const servedByDevice = useMemo(() => {
    const served = new Map<string, CentinelaUnit>();
    for (const unit of centinelaUnits.data?.units ?? []) served.set(unit.device_id, unit);
    return served;
  }, [centinelaUnits.data]);

  const labelOfCase = useCallback(
    (item: CentinelaCaseView) =>
      item.threatId === null
        ? t(`monitoreoIa.caseEvents.origin.${item.origin}`, { defaultValue: item.kind })
        : threatLabels.get(item.threatId) ?? item.threatId,
    [threatLabels, t],
  );

  const rows = useMemo<MonitoringUnitRow[]>(
    () =>
      fleet.watchedUnits
        .map(({ unit, watch }) => {
          const deviceId = watch.deviceId;
          const openCase = deviceId ? openCaseByDevice.get(deviceId) ?? null : null;
          const served = deviceId ? servedByDevice.get(deviceId) ?? null : null;
          return {
            unitId: unit.id,
            deviceId,
            name: unit.name,
            plate: unit.plate ?? served?.plate ?? null,
            driver: unit.driver?.name ?? served?.driver ?? null,
            status: watch.status,
            speedKph: watch.speedKph,
            headingDeg: watch.headingDeg ?? null,
            driverPhoto: watch.driverPhoto ?? null,
            address: watch.address,
            updatedAt: watch.updatedAt,
            position: watch.position,
            hasOperatorPhone: watch.hasOperatorPhone,
            underCustody: watch.underCustody,
            isCalling: watch.isCalling,
            fuelPercent: watch.fuelPercent,
            chips: watch.chips,
            aiMessage: watch.aiMessage,
            aiMessageType: watch.aiMessageType,
            openCase,
            openCaseLabel: openCase ? labelOfCase(openCase) : null,
            trip: served?.trip ?? null,
            unit,
          };
        })
        .sort(
          (a, b) => byWatchSeverity(a.status, b.status) || a.name.localeCompare(b.name),
        ),
    [fleet.watchedUnits, openCaseByDevice, labelOfCase, servedByDevice],
  );

  const rowByDevice = useMemo(() => {
    const index = new Map<string, MonitoringUnitRow>();
    rows.forEach((row) => {
      if (row.deviceId) index.set(row.deviceId, row);
    });
    return index;
  }, [rows]);

  const liveOf = useCallback(
    (deviceId: string) => rowByDevice.get(deviceId) ?? null,
    [rowByDevice],
  );

  const activeThreat = useMemo(
    () => threats.find((threat) => threat.id === threatId) ?? null,
    [threats, threatId],
  );

  const visibleRows = useMemo(() => {
    if (!activeThreat) return rows;
    return rows.filter((row) => row.deviceId && activeThreat.deviceIds.has(row.deviceId));
  }, [rows, activeThreat]);

  const geofences = useMemo<MonitoringGeofence[]>(
    () =>
      (geofencesQuery.data?.data ?? []).flatMap((geofence) => {
        const geometry = geofenceGeometry(geofence);
        if (!geometry) return [];
        return [
          {
            id: geofence.id,
            name: geofence.name,
            type: geofence.type,
            geometry,
            source: geofence,
          },
        ];
      }),
    [geofencesQuery.data],
  );

  const corridors = useMemo<MonitoringCorridor[]>(() => {
    const wanted = new Set(
      (tripsQuery.data?.data ?? [])
        .filter((trip) => trip.route_id && !trip.finalizedAt)
        .map((trip) => trip.route_id as string),
    );

    return (routesQuery.data?.data ?? []).flatMap((route) => {
      if (!wanted.has(route.id)) return [];
      const path = routePath(route);
      if (path.length < 2) return [];
      return [{ id: route.id, name: route.name, path }];
    });
  }, [tripsQuery.data, routesQuery.data]);

  const selectedRow = useMemo(
    () => visibleRows.find((row) => row.unitId === selectedUnitId) ?? null,
    [visibleRows, selectedUnitId],
  );

  const selectedCase = useMemo(() => {
    if (!selectedCaseId || !selectedRow) return null;
    const item = cases.find((entry) => entry.id === selectedCaseId) ?? null;
    return item && item.deviceId === selectedRow.deviceId ? item : null;
  }, [cases, selectedCaseId, selectedRow]);

  const selectUnit = useCallback((unitId: string | null) => {
    setSelectedUnitId(unitId);
    if (unitId) setSelectedCaseId(null);
  }, []);

  /*
    ── De dónde venía quien abrió el caso ────────────────────────────────────
    Abrir un caso selecciona su unidad, porque el mapa tiene que centrarse en
    ella. El efecto colateral era que al CERRAR el caso quedaba la unidad
    seleccionada y aparecía su ficha — aunque quien abrió el caso viniera de la
    inteligencia en vivo y esperara volver a la lista.

    Se recuerda qué unidad había antes y se restaura al cerrar. Así el camino de
    vuelta es siempre el de ida: desde la lista se vuelve a la lista, y desde la
    ficha de una unidad se vuelve a su ficha.
  */
  const unidadAntesDelCaso = useRef<string | null>(null);

  const selectCase = useCallback(
    (item: CentinelaCaseView) => {
      const row = rowByDevice.get(item.deviceId);
      if (!row) return;
      unidadAntesDelCaso.current = selectedUnitId;
      setThreatId((current) =>
        current && !activeThreat?.deviceIds.has(item.deviceId) ? null : current,
      );
      setSelectedUnitId(row.unitId);
      setSelectedCaseId(item.id);
    },
    [rowByDevice, activeThreat, selectedUnitId],
  );

  /** Cierra la tarjeta del caso y devuelve la selección a donde estaba. */
  const clearCase = useCallback(() => {
    setSelectedCaseId(null);
    setSelectedUnitId(unidadAntesDelCaso.current);
    unidadAntesDelCaso.current = null;
  }, []);

  const selectThreat = useCallback((id: string | null) => {
    setThreatId(id);
    setSelectedUnitId(null);
    setSelectedCaseId(null);
  }, []);

  const refresh = useCallback(() => {
    fleet.refresh();
    void casesQuery.refetch();
    void geofencesQuery.refetch();
    void routesQuery.refetch();
    void tripsQuery.refetch();
  }, [fleet, casesQuery, geofencesQuery, routesQuery, tripsQuery]);

  return {
    rows,
    visibleRows,
    selectedRow,
    selectedUnitId,
    selectedCase,
    selectedCaseId,
    selectUnit,
    selectCase,
    clearCase,
    liveOf,
    threats,
    labelOfCase,
    activeThreat,
    threatId,
    selectThreat,
    refresh,
    cases,
    openCases,
    geofences,
    corridors,
    breakdown: fleet.breakdown,
    totalUnits: rows.length,
    isLoading: fleet.isLoading,
    isRefreshing:
      fleet.isRefreshing ||
      casesQuery.isFetching ||
      geofencesQuery.isFetching ||
      routesQuery.isFetching ||
      tripsQuery.isFetching,
    casesLoading: casesQuery.isLoading,
    casesUnavailable: casesQuery.isError,
    isError: fleet.isError,
    error: fleet.error,
  };
}
